import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { normalizeString } from "helpers/normalizeString";
import type { IAtributoOpcao, TipoAtributo } from "interfaces/loja";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../atributos.theme";
import { isHexColor } from "./atributos.helpers";

type Props = {
  open: boolean;
  tipoAtributo: TipoAtributo;
  editing: IAtributoOpcao | null;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: { label: string; valor: string }) => void;
};

export default function OpcaoFormDialog({
  open,
  tipoAtributo,
  editing,
  saving,
  onClose,
  onSave,
}: Props) {
  const [label, setLabel] = useState("");
  const [valor, setValor] = useState("");
  const [valorManual, setValorManual] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel(editing?.label ?? "");
    setValor(editing?.valor ?? "");
    setValorManual(Boolean(editing?.valor));
  }, [open, editing]);

  const handleLabelChange = (next: string) => {
    setLabel(next);
    if (!valorManual && !editing) {
      setValor(normalizeString(next));
    }
    if (tipoAtributo === "COR" && !valorManual && !editing && next.startsWith("#")) {
      setValor(next.trim());
    }
  };

  const handleSave = () => {
    if (!label.trim()) return;
    onSave({
      label: label.trim(),
      valor: valor.trim() || normalizeString(label),
    });
  };

  const showColorPreview = tipoAtributo === "COR" && isHexColor(valor);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { ...modalPaperSx, maxWidth: 480 } }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LabelOutlinedIcon sx={{ fontSize: 28, opacity: 0.9 }} />
            <Typography variant="h6" fontWeight="bold">
              {editing ? "Editar opção" : "Nova opção"}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3 }}>
        <Stack spacing={2}>
          <TextField
            label="Nome exibido (label)"
            value={label}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder={tipoAtributo === "COR" ? "Ex.: Vermelho" : "Ex.: Médio"}
            fullWidth
            size="small"
            sx={fieldSx}
            autoFocus
          />
          <TextField
            label="Valor técnico"
            value={valor}
            onChange={(e) => {
              setValorManual(true);
              setValor(e.target.value);
            }}
            placeholder={
              tipoAtributo === "COR" ? "Ex.: #FF0000 ou codigo-cor" : "Slug interno da opção"
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
          {showColorPreview ? (
            <MDBox display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: valor,
                  border: "1px solid",
                  borderColor: "grey.300",
                }}
              />
              <MDTypography variant="caption" color="text.secondary">
                Prévia da cor
              </MDTypography>
            </MDBox>
          ) : null}
        </Stack>
      </MDBox>

      <MDBox
        px={3}
        py={2}
        display="flex"
        justifyContent="flex-end"
        gap={1}
        borderTop="1px solid"
        borderColor="grey.200"
      >
        <MDButton variant="outlined" color="dark" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleSave}
          disabled={saving || !label.trim()}
          sx={saveButtonSx}
        >
          {saving ? "Salvando…" : "Salvar opção"}
        </MDButton>
      </MDBox>
    </Dialog>
  );
}
